/* eslint-disable @typescript-eslint/no-non-null-assertion */
// the adapter is to bridge the workspace rootdoc & native js bindings
import { createYProxy, type Y } from "@blocksuite/store";
import type { WorkspaceService } from "@toeverything/infra";
import { LiveData, Service } from "@toeverything/infra";
import { defaultsDeep } from "lodash-es";
import { Observable } from "rxjs";
import * as YLib from "yjs";

import type { FavoriteService, FavoriteSupportType } from "../../favorite";
import { generateStableAttendeeId, normalizeEmail } from "./attendee-id-utils";
import {
	AttendeeSchema,
	AttendeesValueSchema,
	PagePropertyType,
	PageSystemPropertyId,
	type SerializedAttendee,
	type SerializedTodoItem,
	type WorkspaceAffineProperties,
} from "./schema";

export const AFFINE_PROPERTIES_ID = "affine:workspace-properties";
const ACTIVE_RECORDING_KEY = "activeRecording";
const AUGMENTED_DOC_SUFFIX = "-augmented";

function canonicalPagePropertiesId(pageId: string): string {
	return pageId.endsWith(AUGMENTED_DOC_SUFFIX)
		? pageId.slice(0, -AUGMENTED_DOC_SUFFIX.length)
		: pageId;
}

export interface ActiveRecording {
	docId: string;
	eventId: string;
	deviceType: string;
	startedAt: number;
}

/**
 * Safe reader for array values from Y.js proxy properties.
 * Y.js proxies can return truthy non-array objects — this is the ONLY
 * correct way to read array `.value` from workspace properties.
 */
export function readProxyArrayValue<T>(value: unknown): T[] {
	return Array.isArray(value) ? value : [];
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === "object" &&
		value !== null &&
		Object.getPrototypeOf(value) === Object.prototype
	);
}

function toSyncedPropertyValue(value: unknown): unknown {
	if (value instanceof Date) return value.toISOString();
	if (Array.isArray(value)) return value.map(toSyncedPropertyValue);
	if (isPlainRecord(value)) {
		return Object.fromEntries(
			Object.entries(value)
				.filter(([, nested]) => nested !== undefined)
				.map(([key, nested]) => [key, toSyncedPropertyValue(nested)]),
		);
	}
	return value;
}

function syncedValueEquals(current: unknown, next: unknown): boolean {
	const normalized =
		current instanceof YLib.AbstractType ? current.toJSON() : current;
	return JSON.stringify(normalized) === JSON.stringify(next);
}

function createItemYMap(item: Record<string, unknown>): YLib.Map<unknown> {
	const itemMap = new YLib.Map<unknown>();
	for (const [key, value] of Object.entries(item)) {
		if (value === undefined) continue;
		itemMap.set(key, toSyncedPropertyValue(value));
	}
	return itemMap;
}

function applyItemFieldUpdates(
	itemMap: YLib.Map<unknown>,
	updates: Record<string, unknown>,
): void {
	for (const [key, value] of Object.entries(updates)) {
		if (value === undefined) {
			itemMap.delete(key);
		} else {
			const next = toSyncedPropertyValue(value);
			if (syncedValueEquals(itemMap.get(key), next)) continue;
			itemMap.set(key, next);
		}
	}
}

/**
 * Fine-grained Y mutations run as non-local transactions so BlockSuite proxies
 * apply them through their remote-update observers — the same path mobile
 * edits already take — keeping proxy reads consistent.
 */
function transactWorkspaceProperties(
	properties: YLib.Map<unknown>,
	fn: () => void,
): void {
	const doc = properties.doc;
	if (!doc) {
		throw new Error("Workspace properties are not attached to a Y.Doc");
	}
	YLib.transact(doc as unknown as YLib.Doc, fn, null, false);
}

function getSystemPropertyYMap(
	properties: YLib.Map<unknown>,
	pageId: string,
	propertyId: string,
): YLib.Map<unknown> | null {
	const pageProperties = properties.get("pageProperties");
	if (!(pageProperties instanceof YLib.Map)) return null;
	const pageProps = pageProperties.get(pageId);
	if (!(pageProps instanceof YLib.Map)) return null;
	const system = pageProps.get("system");
	if (!(system instanceof YLib.Map)) return null;
	const prop = system.get(propertyId);
	return prop instanceof YLib.Map ? prop : null;
}

function getSystemPropertyYArray(
	properties: YLib.Map<unknown>,
	pageId: string,
	propertyId: string,
): YLib.Array<unknown> | null {
	const prop = getSystemPropertyYMap(properties, pageId, propertyId);
	if (!prop) return null;
	const value = prop.get("value");
	return value instanceof YLib.Array ? value : null;
}

function getActionItemsYArray(
	properties: YLib.Map<unknown>,
	pageId: string,
): YLib.Array<unknown> | null {
	return getSystemPropertyYArray(
		properties,
		pageId,
		PageSystemPropertyId.ActionItems,
	);
}

function findActionItemYMap(
	items: YLib.Array<unknown>,
	itemId: string,
): { map: YLib.Map<unknown>; index: number } | null {
	for (let i = 0; i < items.length; i++) {
		const item = items.get(i);
		if (item instanceof YLib.Map && item.get("id") === itemId) {
			return { map: item, index: i };
		}
	}
	return null;
}

export function updateActionItemFields(
	properties: YLib.Map<unknown>,
	pageId: string,
	itemId: string,
	updates: Partial<SerializedTodoItem>,
): boolean {
	const items = getActionItemsYArray(properties, pageId);
	if (!items) return false;
	const found = findActionItemYMap(items, itemId);
	if (!found) return false;
	transactWorkspaceProperties(properties, () => {
		applyItemFieldUpdates(found.map, updates);
	});
	return true;
}

function attendeeStableKey(entry: YLib.Map<unknown>): string | null {
	const email = entry.get("email");
	if (typeof email === "string" && email.trim()) {
		return generateStableAttendeeId(normalizeEmail(email));
	}
	const id = entry.get("id");
	return typeof id === "string" ? id : null;
}

function attendeeKeyOf(attendee: SerializedAttendee): string {
	if (attendee.email?.trim()) {
		return generateStableAttendeeId(normalizeEmail(attendee.email));
	}
	return attendee.id;
}

function findAttendeeYMap(
	items: YLib.Array<unknown>,
	attendeeId: string,
): { map: YLib.Map<unknown>; index: number } | null {
	for (let i = 0; i < items.length; i++) {
		const entry = items.get(i);
		if (!(entry instanceof YLib.Map)) continue;
		if (entry.get("id") === attendeeId) return { map: entry, index: i };
		if (attendeeStableKey(entry) === attendeeId) return { map: entry, index: i };
	}
	return null;
}

export function reconcileAttendees(
	properties: YLib.Map<unknown>,
	pageId: string,
	desired: SerializedAttendee[],
): void {
	const prop = getSystemPropertyYMap(
		properties,
		pageId,
		PageSystemPropertyId.Attendees,
	);
	if (!prop) {
		throw new Error(`Attendees property is missing for page ${pageId}`);
	}
	transactWorkspaceProperties(properties, () => {
		const existing = prop.get("value");
		if (!(existing instanceof YLib.Array)) {
			const fresh = new YLib.Array<unknown>();
			fresh.push(desired.map(createItemYMap));
			prop.set("value", fresh);
			return;
		}
		const desiredKeys = new Set(desired.map(attendeeKeyOf));
		for (let i = existing.length - 1; i >= 0; i--) {
			const entry = existing.get(i);
			if (entry instanceof YLib.Map) {
				const key = attendeeStableKey(entry);
				if (key !== null && desiredKeys.has(key)) continue;
			}
			existing.delete(i, 1);
		}
		for (const attendee of desired) {
			const found = findAttendeeYMap(existing, attendeeKeyOf(attendee));
			if (!found) {
				existing.push([createItemYMap(attendee)]);
				continue;
			}
			applyItemFieldUpdates(
				found.map,
				Object.fromEntries(
					Object.entries(attendee).filter(([, value]) => value !== undefined),
				),
			);
		}
	});
}

/**
 * Diffs the desired item list against the stored Y.Array and applies only
 * per-item, per-field changes so concurrent edits from other devices merge
 * instead of being clobbered by a whole-array replacement.
 */
export function reconcileActionItems(
	properties: YLib.Map<unknown>,
	pageId: string,
	desired: SerializedTodoItem[],
): void {
	const prop = getSystemPropertyYMap(
		properties,
		pageId,
		PageSystemPropertyId.ActionItems,
	);
	if (!prop) {
		throw new Error(`Action items property is missing for page ${pageId}`);
	}
	transactWorkspaceProperties(properties, () => {
		const existing = prop.get("value");
		if (!(existing instanceof YLib.Array)) {
			const fresh = new YLib.Array<unknown>();
			fresh.push(desired.map(createItemYMap));
			prop.set("value", fresh);
			return;
		}
		const desiredIds = new Set(desired.map((item) => item.id));
		for (let i = existing.length - 1; i >= 0; i--) {
			const entry = existing.get(i);
			if (
				entry instanceof YLib.Map &&
				desiredIds.has(String(entry.get("id")))
			) {
				continue;
			}
			existing.delete(i, 1);
		}
		for (const item of desired) {
			const found = findActionItemYMap(existing, item.id);
			if (!found) {
				existing.push([createItemYMap(item)]);
				continue;
			}
			applyItemFieldUpdates(
				found.map,
				Object.fromEntries(
					Object.entries(item).filter(([, value]) => value !== undefined),
				),
			);
		}
	});
}

/**
 * WorkspacePropertiesAdapter is a wrapper for workspace properties.
 * Users should not directly access the workspace properties via yjs, but use this adapter instead.
 *
 * Question for enhancement in the future:
 * May abstract the adapter for each property type, e.g. PagePropertiesAdapter, SchemaAdapter, etc.
 * So that the adapter could be more focused and easier to maintain (like assigning default values)
 * However the properties for an abstraction may not be limited to a single yjs map.
 */
export class WorkspacePropertiesAdapter extends Service {
	public readonly proxy: WorkspaceAffineProperties;
	public readonly properties: Y.Map<unknown>;
	public readonly properties$: LiveData<WorkspaceAffineProperties>;

	private ensuredRoot = false;
	private ensuredPages = {} as Record<string, boolean>;

	get workspace() {
		return this.workspaceService.workspace;
	}

	private get isReady(): boolean {
		return this.properties.doc !== null;
	}

	constructor(public readonly workspaceService: WorkspaceService) {
		super();
		const rootDoc = workspaceService.workspace.docCollection.doc;
		this.properties = rootDoc.getMap(AFFINE_PROPERTIES_ID);
		this.proxy = createYProxy(this.properties);

		this.properties$ = LiveData.from(
			new Observable((observer) => {
				let scheduled = false;
				const scheduleUpdate = () => {
					if (scheduled) return;
					scheduled = true;
					requestAnimationFrame(() => {
						scheduled = false;
						if (!this.isReady) return;
						try {
							observer.next(new Proxy(this.proxy, {}));
						} catch {
							// Yjs type not attached yet
						}
					});
				};
				scheduleUpdate();
				this.properties.observeDeep(scheduleUpdate);
				return () => {
					this.properties.unobserveDeep(scheduleUpdate);
				};
			}),
			{} as WorkspaceAffineProperties,
		);

		this.activeRecording$ = LiveData.from(
			new Observable<ActiveRecording | null>((observer) => {
				const emit = () => {
					observer.next(this.parseActiveRecording());
				};
				emit();
				this.properties.observeDeep(emit);
				return () => {
					this.properties.unobserveDeep(emit);
				};
			}),
			null,
		);
	}

	public readonly activeRecording$: LiveData<ActiveRecording | null>;

	private parseActiveRecording(): ActiveRecording | null {
		const rec = this.properties.get(ACTIVE_RECORDING_KEY);
		if (!(rec instanceof YLib.Map)) return null;
		const docId = rec.get("docId");
		const eventId = rec.get("eventId");
		const deviceType = rec.get("deviceType");
		const startedAt = rec.get("startedAt");
		if (
			typeof docId !== "string" ||
			typeof eventId !== "string" ||
			typeof deviceType !== "string" ||
			typeof startedAt !== "number"
		)
			return null;
		// Stale recordings (> 6h) are auto-cleaned
		if (Date.now() - startedAt > 6 * 60 * 60 * 1000) {
			this.properties.delete(ACTIVE_RECORDING_KEY);
			return null;
		}
		return { docId, eventId, deviceType, startedAt };
	}

	setActiveRecording(docId: string, eventId: string): void {
		this.transact(() => {
			const rec = new YLib.Map<unknown>();
			rec.set("docId", docId);
			rec.set("eventId", eventId);
			rec.set("deviceType", "desktop");
			rec.set("startedAt", Date.now());
			this.properties.set(ACTIVE_RECORDING_KEY, rec);
		});
	}

	clearActiveRecording(docId: string): void {
		this.transact(() => {
			const existing = this.parseActiveRecording();
			if (existing?.docId === docId) {
				this.properties.delete(ACTIVE_RECORDING_KEY);
			}
		});
	}

	public ensureRootProperties() {
		if (this.ensuredRoot || !this.isReady) {
			return;
		}
		this.ensuredRoot = true;
		defaultsDeep(this.proxy, {
			schema: {
				pageProperties: {
					custom: {},
					system: {
						journal: {
							id: PageSystemPropertyId.Journal,
							name: "Journal",
							source: "system",
							type: PagePropertyType.Date,
						},
						tags: {
							id: PageSystemPropertyId.Tags,
							name: "Tags",
							source: "system",
							type: PagePropertyType.Tags,
							options:
								this.workspaceService.workspace.docCollection.meta.properties
									.tags?.options ?? [], // better use a one time migration
						},
						actionItems: {
							id: PageSystemPropertyId.ActionItems,
							name: "Action Items",
							source: "system",
							type: PagePropertyType.Text,
						},
						attendees: {
							id: PageSystemPropertyId.Attendees,
							name: "Attendees",
							source: "system",
							type: PagePropertyType.Attendees,
						},
						followupEmailStatus: {
							id: PageSystemPropertyId.FollowupEmailStatus,
							name: "Follow-up Email Status",
							source: "system",
							type: PagePropertyType.Text,
						},
						augmentationStatus: {
							id: PageSystemPropertyId.AugmentationStatus,
							name: "Augmentation Status",
							source: "system",
							type: PagePropertyType.Text,
						},
					},
				},
			},
			pageProperties: {},
		});
	}

	ensurePageProperties(pageId: string) {
		if (!this.isReady) {
			return;
		}
		this.ensureRootProperties();
		if (this.ensuredPages[pageId]) {
			return;
		}
		this.ensuredPages[pageId] = true;
		defaultsDeep(this.proxy.pageProperties, {
			[pageId]: {
				custom: {},
				system: {
					[PageSystemPropertyId.Journal]: {
						id: PageSystemPropertyId.Journal,
						value: false,
					},
					[PageSystemPropertyId.Tags]: {
						id: PageSystemPropertyId.Tags,
						value: [],
					},
					[PageSystemPropertyId.ActionItems]: {
						id: PageSystemPropertyId.ActionItems,
						value: [],
					},
					[PageSystemPropertyId.Attendees]: {
						id: PageSystemPropertyId.Attendees,
						value: [],
					},
					[PageSystemPropertyId.Overview]: {
						id: PageSystemPropertyId.Overview,
						value: "",
					},
					[PageSystemPropertyId.FollowupEmailStatus]: {
						id: PageSystemPropertyId.FollowupEmailStatus,
						value: "NotStarted",
					},
					[PageSystemPropertyId.AugmentationStatus]: {
						id: PageSystemPropertyId.AugmentationStatus,
						value: "NotStarted",
					},
					[PageSystemPropertyId.SocialFeed]: {
						id: PageSystemPropertyId.SocialFeed,
						value: [],
					},
					[PageSystemPropertyId.MeetingPrepSummary]: {
						id: PageSystemPropertyId.MeetingPrepSummary,
						value: {
							briefSummary: "",
							topicsLikelyToComeUp: [],
							selectedAttendeeEmails: [],
							sourceDocIds: [],
						},
					},
					[PageSystemPropertyId.PendingAugmentation]: {
						id: PageSystemPropertyId.PendingAugmentation,
						value: null,
					},
				},
			},
		});
	}

	transact = this.workspaceService.workspace.docCollection.doc.transact.bind(
		this.workspaceService.workspace.docCollection.doc,
	);

	get schema() {
		return this.proxy.schema;
	}

	/**
	 * @deprecated
	 */
	get favorites() {
		return this.proxy.favorites;
	}

	get pageProperties() {
		return this.proxy.pageProperties;
	}

	// ====== utilities ======

	getPageProperties(pageId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		return this.pageProperties?.[pageId] ?? null;
	}

	getJournalPageDateString(id: string) {
		id = canonicalPagePropertiesId(id);
		return this.pageProperties?.[id]?.system[PageSystemPropertyId.Journal]
			?.value;
	}

	setJournalPageDateString(id: string, date: string) {
		id = canonicalPagePropertiesId(id);
		this.ensurePageProperties(id);
		const pageProperties = this.pageProperties?.[id];
		pageProperties!.system[PageSystemPropertyId.Journal].value = date;
	}

	/**
	 * After the user completes the migration, call this function to clear the favorite data
	 */
	markFavoritesMigrated() {
		this.proxy.favoritesMigrated = true;
	}

	/**
	 * Get action items for a specific page
	 */
	getPageActionItems(pageId: string): SerializedTodoItem[] {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		return readProxyArrayValue<SerializedTodoItem>(
			pageProperties?.system[PageSystemPropertyId.ActionItems]?.value,
		);
	}

	/**
	 * Set action items for a specific page. Reconciles per item so concurrent
	 * edits from other devices merge instead of being clobbered.
	 */
	setPageActionItems(pageId: string, items: SerializedTodoItem[]) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		reconcileActionItems(this.properties, pageId, items);
	}

	/**
	 * Add an action item to a specific page
	 */
	addPageActionItem(pageId: string, item: SerializedTodoItem) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const items = getActionItemsYArray(this.properties, pageId);
		if (!items) {
			throw new Error(
				`Action items array is missing for page ${pageId} after ensurePageProperties`,
			);
		}
		if (findActionItemYMap(items, item.id)) return;
		transactWorkspaceProperties(this.properties, () => {
			items.push([createItemYMap(item)]);
		});
	}

	/**
	 * Remove an action item from a specific page
	 */
	removePageActionItem(pageId: string, itemId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const items = getActionItemsYArray(this.properties, pageId);
		if (!items) return;
		const found = findActionItemYMap(items, itemId);
		if (!found) return;
		transactWorkspaceProperties(this.properties, () => {
			items.delete(found.index, 1);
		});
	}

	/**
	 * Update an action item on a specific page
	 */
	updatePageActionItem(
		pageId: string,
		itemId: string,
		updates: Partial<SerializedTodoItem>,
	) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		updateActionItemFields(this.properties, pageId, itemId, updates);
	}

	/**
	 * Get attendees for a specific page (raw data)
	 */
	getPageAttendees(pageId: string): SerializedAttendee[] {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		return readProxyArrayValue<SerializedAttendee>(
			pageProperties?.system[PageSystemPropertyId.Attendees]?.value,
		).map((attendee) =>
			attendee.id || !attendee.email
				? attendee
				: { ...attendee, id: generateStableAttendeeId(attendee.email) },
		);
	}

	/**
	 * Set attendees for a specific page. Reconciles per attendee so concurrent
	 * edits from other devices merge instead of being clobbered.
	 */
	setPageAttendees(pageId: string, attendees: SerializedAttendee[]) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		reconcileAttendees(
			this.properties,
			pageId,
			AttendeesValueSchema.parse(attendees),
		);
	}

	/**
	 * Add an attendee to a specific page (raw operation)
	 * Note: Use AttendeesService.addPageAttendee() for business logic
	 */
	addPageAttendee(pageId: string, attendee: SerializedAttendee) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.Attendees,
		);
		if (!items) {
			throw new Error(
				`Attendees array is missing for page ${pageId} after ensurePageProperties`,
			);
		}
		const parsed = AttendeeSchema.parse(attendee);
		if (findAttendeeYMap(items, attendeeKeyOf(parsed))) return;
		transactWorkspaceProperties(this.properties, () => {
			items.push([createItemYMap(parsed)]);
		});
	}

	/**
	 * Remove an attendee from a specific page by ID (raw operation)
	 */
	removePageAttendee(pageId: string, attendeeId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.Attendees,
		);
		if (!items) return;
		const found = findAttendeeYMap(items, attendeeId);
		if (!found) return;
		transactWorkspaceProperties(this.properties, () => {
			items.delete(found.index, 1);
		});
	}

	/**
	 * Update an attendee on a specific page (raw operation)
	 */
	updatePageAttendee(
		pageId: string,
		attendeeId: string,
		updates: Partial<SerializedAttendee>,
	) {
		pageId = canonicalPagePropertiesId(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.Attendees,
		);
		if (!items) return;
		const found = findAttendeeYMap(items, attendeeId);
		if (!found) return;
		transactWorkspaceProperties(this.properties, () => {
			applyItemFieldUpdates(found.map, updates);
		});
	}

	// ===== Follow-up Email Status helpers =====
	getPageFollowupEmailStatus(pageId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		const v =
			pageProperties?.system[PageSystemPropertyId.FollowupEmailStatus]?.value;
		return (v ?? "NotStarted") as "NotStarted" | "Draft" | "Copied";
	}

	setPageFollowupEmailStatus(
		pageId: string,
		status: "NotStarted" | "Draft" | "Copied",
	) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		pageProperties!.system[PageSystemPropertyId.FollowupEmailStatus].value =
			status;
	}

	// ===== Augmentation Status helpers =====
	getPageAugmentationStatus(pageId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		const v =
			pageProperties?.system[PageSystemPropertyId.AugmentationStatus]?.value;
		return (v ?? "NotStarted") as "NotStarted" | "Original" | "Augmented";
	}

	setPageAugmentationStatus(
		pageId: string,
		status: "NotStarted" | "Original" | "Augmented",
	) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		pageProperties!.system[PageSystemPropertyId.AugmentationStatus].value =
			status;
	}

	// ===== Overview (high-level meeting summary) helpers =====
	getPageOverview(pageId: string): string {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		const v = pageProperties?.system[PageSystemPropertyId.Overview]?.value;
		return (v ?? "") as string;
	}

	pageOverview$(pageId: string): LiveData<string> {
		return this.properties$
			.map(() => this.getPageOverview(pageId))
			.distinctUntilChanged();
	}

	setPageOverview(pageId: string, text: string) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		pageProperties!.system[PageSystemPropertyId.Overview].value = text ?? "";
	}

	// ===== Social Feed helpers =====
	getPageSocialFeed(pageId: string) {
		pageId = canonicalPagePropertiesId(pageId);
		const pageProperties = this.pageProperties?.[pageId];
		return readProxyArrayValue<{
			id: string;
			attendeeEmail: string;
			attendeeName: string;
			text: string;
			confidence: number;
			source?: string;
			timestamp?: string;
		}>(pageProperties?.system[PageSystemPropertyId.SocialFeed]?.value);
	}

	setPageSocialFeed(
		pageId: string,
		items: Array<{
			id: string;
			attendeeEmail: string;
			attendeeName: string;
			text: string;
			confidence: number;
			source?: string;
			timestamp?: string;
		}>,
	) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const prop = getSystemPropertyYMap(
			this.properties,
			pageId,
			PageSystemPropertyId.SocialFeed,
		);
		if (!prop) {
			throw new Error(`Social feed property is missing for page ${pageId}`);
		}
		const desired = items ?? [];
		transactWorkspaceProperties(this.properties, () => {
			const existing = prop.get("value");
			if (!(existing instanceof YLib.Array)) {
				const fresh = new YLib.Array<unknown>();
				fresh.push(desired.map(createItemYMap));
				prop.set("value", fresh);
				return;
			}
			const desiredIds = new Set(desired.map((item) => item.id));
			for (let i = existing.length - 1; i >= 0; i--) {
				const entry = existing.get(i);
				if (
					entry instanceof YLib.Map &&
					desiredIds.has(String(entry.get("id")))
				) {
					continue;
				}
				existing.delete(i, 1);
			}
			for (const item of desired) {
				const found = findActionItemYMap(existing, item.id);
				if (!found) {
					existing.push([createItemYMap(item)]);
					continue;
				}
				applyItemFieldUpdates(
					found.map,
					Object.fromEntries(
						Object.entries(item).filter(([, value]) => value !== undefined),
					),
				);
			}
		});
	}

	addPageSocialFeedItem(
		pageId: string,
		item: {
			id: string;
			attendeeEmail: string;
			attendeeName: string;
			text: string;
			confidence: number;
			source?: string;
			timestamp?: string;
		},
	) {
		pageId = canonicalPagePropertiesId(pageId);
		this.ensurePageProperties(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.SocialFeed,
		);
		if (!items) {
			throw new Error(
				`Social feed array is missing for page ${pageId} after ensurePageProperties`,
			);
		}
		if (findActionItemYMap(items, item.id)) return;
		transactWorkspaceProperties(this.properties, () => {
			items.push([createItemYMap(item)]);
		});
	}

	removePageSocialFeedItem(pageId: string, itemId: string): boolean {
		pageId = canonicalPagePropertiesId(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.SocialFeed,
		);
		if (!items) return false;
		const found = findActionItemYMap(items, itemId);
		if (!found) return false;
		transactWorkspaceProperties(this.properties, () => {
			items.delete(found.index, 1);
		});
		return true;
	}

	updatePageSocialFeedItem(
		pageId: string,
		itemId: string,
		updates: Partial<{
			attendeeEmail: string;
			attendeeName: string;
			text: string;
			confidence: number;
			source?: string;
			timestamp?: string;
		}>,
	): boolean {
		pageId = canonicalPagePropertiesId(pageId);
		const items = getSystemPropertyYArray(
			this.properties,
			pageId,
			PageSystemPropertyId.SocialFeed,
		);
		if (!items) return false;
		const found = findActionItemYMap(items, itemId);
		if (!found) return false;
		transactWorkspaceProperties(this.properties, () => {
			applyItemFieldUpdates(found.map, updates);
		});
		return true;
	}
}

export class MigrationFavoriteItemsAdapter extends Service {
	constructor(private readonly adapter: WorkspacePropertiesAdapter) {
		super();
	}

	favorites$ = this.adapter.properties$.map(() =>
		this.getItems().filter((i) => i.value),
	);

	migrated$ = this.adapter.properties$.map(
		(props: WorkspaceAffineProperties) => props.favoritesMigrated ?? false,
	);

	getItems() {
		return Object.entries(this.adapter.favorites ?? {})
			.filter(([k]) => k.includes(":"))
			.map(([, v]) => v);
	}

	markFavoritesMigrated() {
		this.adapter.markFavoritesMigrated();
	}
}

type CompatibleFavoriteSupportType = FavoriteSupportType;

/**
 * A service written for compatibility,with the same API as old FavoriteItemsAdapter.
 */
export class CompatibleFavoriteItemsAdapter extends Service {
	constructor(private readonly favoriteService: FavoriteService) {
		super();
	}

	toggle(id: string, type: CompatibleFavoriteSupportType) {
		this.favoriteService.favoriteList.toggle(type, id);
	}

	isFavorite$(id: string, type: CompatibleFavoriteSupportType) {
		return this.favoriteService.favoriteList.isFavorite$(type, id);
	}

	isFavorite(id: string, type: CompatibleFavoriteSupportType) {
		return this.favoriteService.favoriteList.isFavorite$(type, id).value;
	}

	get favorites$() {
		return this.favoriteService.favoriteList.list$.map<
			{
				id: string;
				order: string;
				type: "doc" | "collection";
				value: boolean;
			}[]
		>((v: Array<{ id: string; type: FavoriteSupportType; index: string }>) =>
			v
				.filter(
					(i): i is { id: string; type: "doc" | "collection"; index: string } =>
						i.type === "doc" || i.type === "collection",
				)
				.map((i) => ({
					id: i.id,
					order: "",
					type: i.type,
					value: true,
				})),
		);
	}
}
